import { useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { api } from '../api/client'
import type { Contract, ContractSummary, EagleSLA } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { Card, EmptyState, ErrorBanner, Spinner, StatCard } from '../components/common'
import {
  BadgeStatoContratto, CellaScadenza, etichettaPeriodicita, etichettaStatoContratto, etichettaTipoContratto, formattaImporto,
} from '../components/contratti'
import { ModuloContratto, VALORI_VUOTI } from '../components/ModuloContratto'
import { DataTable, type Column } from '../components/DataTable'
import { SelettoreAnagrafica } from '../components/SelettoreAnagrafica'
import { colonnaTag, useTagDi } from '../components/Tag'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'

/**
 * Registro dei contratti.
 *
 * La cosa che questa pagina fa e che un elenco di date non farebbe è distinguere la
 * **scadenza** dalla **data che conta**. Su un contratto a rinnovo tacito la
 * scadenza non è il giorno in cui si resta scoperti: è quello in cui si rinnova da
 * sé, e la data da segnare in calendario è l'ultimo giorno utile per disdire — mesi
 * prima. Confondere le due cose costa un anno di canone.
 *
 * I filtri stanno nell'URL: dalle anagrafiche si arriva qui con
 * `?cost_center_id=…`, e il collegamento si può passare.
 */
const FILTRI = ['q', 'contract_type', 'status', 'vendor', 'cost_center_id', 'org_unit_id', 'location_id'] as const
type Filtro = (typeof FILTRI)[number]

export default function Contracts() {
  const navigate = useNavigate()
  const { principal } = useAuth()
  const puoModificare = principal?.roles.some((r) => ['tenant_admin', 'provider_admin', 'operator'].includes(r)) ?? false
  const [params, setParams] = useSearchParams()
  const filtri = Object.fromEntries(FILTRI.map((k) => [k, params.get(k) ?? ''])) as Record<Filtro, string>
  const chiave = FILTRI.map((k) => filtri[k]).join('|')

  const summary = useAsync(() => api.contractSummary(), [])
  const catalog = useAsync(() => api.contractCatalog(), [])
  const anagrafiche = useAsync(() => api.masterDataTutte(), [])
  const slas = useAsync<EagleSLA[]>(() => api.eagleNotify().then((r) => r.slas).catch(() => []), [])
  const contracts = useAsync(
    () => api.contracts(Object.fromEntries(Object.entries(filtri).filter(([, v]) => v)) as Record<string, string>),
    [chiave],
  )
  const [nuovo, setNuovo] = useState(false)
  const tag = useTagDi('contract')

  const imposta = (k: Filtro, v: string) => {
    const next = new URLSearchParams(params)
    if (v) next.set(k, v)
    else next.delete(k)
    setParams(next, { replace: true })
  }

  const s = summary.data
  const righe = contracts.data ?? []
  const fornitori = useMemo(() => Array.from(new Set((contracts.data ?? []).map((c) => c.vendor))).sort(), [contracts.data])
  const valuta = righe[0]?.currency ?? 'EUR'
  const qualcheFiltro = FILTRI.some((k) => filtri[k])

  const piccolo = { fontSize: 11 }
  const colonneBase: Column<Contract>[] = [
    {
      key: 'reference',
      header: t('Riferimento'),
      mono: true,
      value: (c) => [c.reference, c.name].filter(Boolean).join(' '),
      render: (c) => (
        <>
          <Link to={`/contracts/${c.id}`}>{c.reference}</Link>
          {c.name && <div className="text-muted" style={piccolo}>{c.name}</div>}
        </>
      ),
    },
    ...colonnaTag<Contract>('contract', tag, (c) => c.id),
    {
      key: 'vendor',
      header: t('Fornitore'),
      value: (c) => [c.vendor, c.service_level].filter(Boolean).join(' '),
      render: (c) => (
        <>
          {c.vendor}
          {c.service_level && <div className="text-muted" style={piccolo}>{c.service_level}</div>}
        </>
      ),
    },
    { key: 'contract_type', header: t('Tipo'), value: (c) => etichettaTipoContratto(c.contract_type) },
    {
      key: 'stato',
      header: t('Stato'),
      value: (c) => etichettaStatoContratto(c.effective_status),
      render: (c) => <BadgeStatoContratto stato={c.effective_status} />,
    },
    {
      key: 'end_date',
      header: t('Scadenza'),
      type: 'date',
      value: (c) => c.end_date,
      render: (c) => (
        <>
          {c.end_date ?? '—'}
          {c.auto_renew && <div className="text-muted" style={piccolo}>{t('rinnovo tacito')}</div>}
        </>
      ),
    },
    {
      key: 'deadline',
      header: t('Data che conta'),
      type: 'date',
      value: (c) => c.deadline,
      render: (c) => <CellaScadenza contratto={c} />,
    },
    { key: 'assets_covered', header: t('Beni'), type: 'number', value: (c) => c.assets_covered },
    {
      key: 'annual_value',
      header: t('Valore annuo'),
      type: 'number',
      value: (c) => c.annual_value,
      render: (c) => (
        <>
          {formattaImporto(c.annual_value, c.currency)}
          {c.value !== null && c.billing_period !== 'annual' && (
            <div className="text-muted" style={piccolo}>{formattaImporto(c.value, c.currency)} · {etichettaPeriodicita(c.billing_period)}</div>
          )}
        </>
      ),
    },
    {
      key: 'imputazione',
      header: t('Imputazione'),
      value: (c) => [c.cost_center_name, c.org_unit_name, c.owner].filter(Boolean).join(' · '),
      render: (c) => (
        <>
          {c.cost_center_name ?? <span className="text-muted">—</span>}
          {(c.org_unit_name || c.owner) && (
            <div className="text-muted" style={piccolo}>{[c.org_unit_name, c.owner].filter(Boolean).join(' · ')}</div>
          )}
        </>
      ),
    },
  ]
  // Renewed and terminated contracts stay listed but dimmed, as before.
  const spento = (c: Contract) => c.status === 'renewed' || c.status === 'terminated'
  const colonne: Column<Contract>[] = colonneBase.map((col) => ({
    ...col,
    render: (c: Contract) => {
      const cella = col.render ? col.render(c) : (col.value?.(c) ?? '—')
      return spento(c) ? <div style={{ opacity: 0.7 }}>{cella}</div> : cella
    },
  }))

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Contratti')}</h1>
          <p className="page__subtitle">
            {t('Gestisce contratti di manutenzione, supporto, noleggio e licenza, associandoli agli asset coperti per garantirne la continuità indipendentemente dal CI.')}
          </p>
        </div>
        {puoModificare && !nuovo && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={() => setNuovo(true)}>{t('Nuovo contratto')}</button>
            <button type="button" className="secondary" onClick={() => scaricaCsv(righe)} disabled={righe.length === 0}
                    title={righe.length === 0 ? t('Nessun contratto da esportare') : undefined}>{t('Esporta CSV')}</button>
          </div>
        )}
      </header>

      <ErrorBanner error={contracts.error ?? summary.error} />

      {s && (
        <div className="stats">
          <StatCard label={t('In vigore')} value={s.active} hint={t('{n} in tutto, {b} bozze', { n: s.total, b: s.draft })} to="/contracts?status=active" />
          <StatCard label={t('In scadenza (90 g)')} value={s.expiring_90d} tone={s.expiring_90d > 0 ? 'warn' : 'ok'} to="/contracts?status=expiring" />
          {/* Separato dal precedente: uno è un promemoria, l'altro è un problema
              in corso. Sommarli farebbe sembrare tutto ugualmente urgente. */}
          <StatCard label={t('Già scaduti')} value={s.already_expired} tone={s.already_expired > 0 ? 'error' : 'ok'} to="/contracts?status=expired" />
          <StatCard label={t('Valore annuo')} value={formattaImporto(s.annual_value, valuta)} hint={t('{n} a rinnovo tacito', { n: s.auto_renew })} />
          <StatCard label={t('Beni scoperti')} value={s.assets_uncovered} hint={t('{n} di {tot} coperti', { n: s.assets_covered, tot: s.assets_total })}
                    tone={s.assets_uncovered > 0 ? 'warn' : 'ok'} to="/assets?uncovered=1" />
        </div>
      )}

      {nuovo && (
        <Card title={t('Nuovo contratto')}>
          <ModuloContratto iniziale={VALORI_VUOTI} catalogo={catalog.data} anagrafiche={anagrafiche.data} slas={slas.data ?? []} nuovo
                           onSalva={async (corpo) => { const c = await api.createContract(corpo); navigate(`/contracts/${c.id}`) }}
                           onAnnulla={() => setNuovo(false)} />
        </Card>
      )}

      {s && s.active > 0 && <Spesa s={s} valuta={valuta} />}

      <Card title={t('Contratti ({n})', { n: righe.length })}>
        <div className="toolbar">
          <div className="field" style={{ minWidth: 220 }}>
            <label htmlFor="c-q">{t('Cerca')}</label>
            <input id="c-q" value={filtri.q} onChange={(e) => imposta('q', e.target.value)} placeholder={t('riferimento, nome, fornitore, responsabile…')} />
          </div>
          <div className="field">
            <label htmlFor="c-type">{t('Tipo')}</label>
            <select id="c-type" value={filtri.contract_type} onChange={(e) => imposta('contract_type', e.target.value)}>
              <option value="">{t('tutti')}</option>
              {(catalog.data?.types ?? []).map((x) => <option key={x.value} value={x.value}>{etichettaTipoContratto(x.value)}</option>)}
            </select>
          </div>
          <div className="field">
            <label htmlFor="c-status">{t('Stato')}</label>
            <select id="c-status" value={filtri.status} onChange={(e) => imposta('status', e.target.value)}>
              <option value="">{t('tutti')}</option>
              <option value="expiring">{t('In scadenza (90 g), compresi gli scaduti')}</option>
              {(catalog.data?.statuses ?? []).map((x) => <option key={x.value} value={x.value}>{etichettaStatoContratto(x.value)}</option>)}
              <option value="expired">{t('Scaduto')}</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="c-vendor">{t('Fornitore')}</label>
            <input id="c-vendor" list="c-fornitori" value={filtri.vendor} onChange={(e) => imposta('vendor', e.target.value)} />
            <datalist id="c-fornitori">{fornitori.map((f) => <option key={f} value={f} />)}</datalist>
          </div>
          <div className="field">
            <label htmlFor="c-cdc">{t('Centro di costo')}</label>
            <SelettoreAnagrafica id="c-cdc" kind="cost_center" voci={anagrafiche.data?.cost_center ?? []} value={filtri.cost_center_id} onChange={(id) => imposta('cost_center_id', id ?? '')} />
          </div>
          <div className="field">
            <label htmlFor="c-ou">{t('Unità organizzativa')}</label>
            <SelettoreAnagrafica id="c-ou" kind="org_unit" voci={anagrafiche.data?.org_unit ?? []} value={filtri.org_unit_id} onChange={(id) => imposta('org_unit_id', id ?? '')} />
          </div>
          <div className="field">
            <label htmlFor="c-loc">{t('Sede')}</label>
            <SelettoreAnagrafica id="c-loc" kind="location" voci={anagrafiche.data?.location ?? []} value={filtri.location_id} onChange={(id) => imposta('location_id', id ?? '')} />
          </div>
          {qualcheFiltro && (
            <button type="button" className="secondary" onClick={() => setParams(new URLSearchParams(), { replace: true })}>{t('Azzera i filtri')}</button>
          )}
        </div>

        {contracts.loading && !contracts.data ? (
          <Spinner />
        ) : righe.length === 0 ? (
          <EmptyState title={qualcheFiltro ? t('Nessun contratto con questi filtri') : t('Nessun contratto')}
                      hint={qualcheFiltro ? undefined : t('Registra un contratto per monitorare scadenze, asset coperti e costi.')} />
        ) : (
          <DataTable
            rows={righe}
            columns={colonne}
            rowKey={(c) => c.id}
            conteggio={{ nome: t('contratti') }}
          />
        )}
      </Card>
    </>
  )
}

/** Dove va la spesa e quando scade: per tipo, per centro di costo, per fornitore, mese per mese. */
function Spesa({ s, valuta }: { s: ContractSummary; valuta: string }) {
  const massimo = Math.max(1, ...s.next_12_months.map((m) => m.count))
  const mese = (ym: string) => {
    const [y, m] = ym.split('-').map(Number)
    return new Date(y, m - 1, 1).toLocaleDateString(localeCorrente(), { month: 'short' })
  }
  return (
    <Card title={t('Spesa e scadenze')}>
      <div className="contratto-spesa">
        <div>
          <h4 className="contratto-spesa__titolo">{t('Per tipo')}</h4>
          <table className="contratto-spesa__tabella">
            <tbody>
              {s.by_type.map((r) => (
                <tr key={r.contract_type}>
                  <td><Link to={`/contracts?contract_type=${r.contract_type}&status=active`}>{etichettaTipoContratto(r.contract_type)}</Link></td>
                  <td className="text-muted">{r.count}</td>
                  <td style={{ textAlign: 'right' }}>{formattaImporto(r.annual_value, valuta)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div>
          <h4 className="contratto-spesa__titolo">{t('Per centro di costo')}</h4>
          <table className="contratto-spesa__tabella">
            <tbody>
              {s.by_cost_center.map((r) => (
                <tr key={r.cost_center_id ?? 'none'}>
                  <td>
                    {r.cost_center_id
                      ? <Link to={`/contracts?cost_center_id=${r.cost_center_id}&status=active`}>{r.cost_center_name}</Link>
                      : <span className="text-muted">{t('senza centro di costo')}</span>}
                  </td>
                  <td className="text-muted">{r.count}</td>
                  <td style={{ textAlign: 'right' }}>{formattaImporto(r.annual_value, valuta)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div>
          <h4 className="contratto-spesa__titolo">{t('Per fornitore')}</h4>
          <table className="contratto-spesa__tabella">
            <tbody>
              {s.by_vendor.map((r) => (
                <tr key={r.vendor}>
                  <td><Link to={`/contracts?vendor=${encodeURIComponent(r.vendor)}&status=active`}>{r.vendor}</Link></td>
                  <td className="text-muted">{r.count}</td>
                  <td style={{ textAlign: 'right' }}>{formattaImporto(r.annual_value, valuta)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div>
          <h4 className="contratto-spesa__titolo">{t('Date che contano nei prossimi 12 mesi')}</h4>
          <div className="contratto-mesi" role="img" aria-label={t('Date che contano nei prossimi 12 mesi')}>
            {s.next_12_months.map((m) => (
              <div key={m.month} className="contratto-mesi__colonna" title={`${m.month}: ${m.count} · ${formattaImporto(m.value, valuta)}`}>
                <div className="contratto-mesi__valore">{m.count > 0 ? m.count : ''}</div>
                <div className="contratto-mesi__barra" style={{ height: `${Math.round((m.count / massimo) * 80)}px` }} />
                <div className="contratto-mesi__etichetta">{mese(m.month)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  )
}

/** L'elenco filtrato, così com'è, in un CSV: per chi lo vuole in un foglio. */
function scaricaCsv(righe: Contract[]) {
  const intestazione = [
    t('Riferimento'), t('Nome'), t('Fornitore'), t('Tipo'), t('Stato'), t('Decorrenza'), t('Scadenza'), t('Rinnovo tacito'),
    t('Giorni di preavviso'), t('Data che conta'), t('Giorni residui'), t('Beni'), t('Valore'), t('Valuta'), t('Periodicità'),
    t('Valore annuo'), t('Centro di costo'), t('Unità organizzativa'), t('Sede'), t('Responsabile'), t('Livello di servizio'),
    t('Riferimento esterno'),
  ]
  const cella = (v: unknown) => {
    const s = v === null || v === undefined ? '' : String(v)
    return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const corpo = righe.map((c) => [
    c.reference, c.name, c.vendor, etichettaTipoContratto(c.contract_type), etichettaStatoContratto(c.effective_status),
    c.start_date, c.end_date, c.auto_renew ? t('sì') : t('no'), c.notice_days, c.deadline, c.days_left, c.assets_covered,
    c.value, c.currency, etichettaPeriodicita(c.billing_period), c.annual_value, c.cost_center_name, c.org_unit_name,
    c.location_name, c.owner, c.service_level, c.external_ref,
  ].map(cella).join(';'))
  const blob = new Blob(['﻿' + [intestazione.map(cella).join(';'), ...corpo].join('\r\n')], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `censo-contratti-${new Date().toISOString().slice(0, 10)}.csv`
  a.click()
  URL.revokeObjectURL(url)
}
